import type { ReactNode } from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { Card } from './Card';

interface MetricCardProps {
  label: string;
  value: string;
  icon?: ReactNode;
  growthPct?: number;
  trendValues?: number[];
}

export function MetricCard({ label, value, icon, growthPct, trendValues }: MetricCardProps) {
  const hasGrowth = typeof growthPct === 'number';
  const isUp = hasGrowth && growthPct >= 0;
  const hasTrend = !!trendValues && trendValues.length >= 2;
  const trendColor = isUp ? '#15803D' : '#B91C1C';

  return (
    <Card className="flex flex-col gap-2.5">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[15px] leading-6 text-text-secondary">{label}</span>
        {icon && <span className="text-text-muted">{icon}</span>}
      </div>

      <span className="text-[32px] leading-9 font-bold text-text-primary tracking-tight tabular-nums">{value}</span>

      {(hasGrowth || hasTrend) && (
        <div className="flex items-center justify-between gap-3">
          {hasGrowth ? (
            <span
              className={`inline-flex items-center gap-0.5 text-[14px] font-semibold ${isUp ? 'text-success' : 'text-danger'}`}
            >
              {isUp ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
              {Math.abs(growthPct).toFixed(1)}%
            </span>
          ) : <span />}

          {hasTrend && (
            <div className="h-7 w-20">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendValues.map((v, i) => ({ i, v }))}>
                  <Line
                    type="monotone"
                    dataKey="v"
                    stroke={trendColor}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
